import {
  type FC,
  type ReactElement,
  type ReactNode,
  useCallback,
  useState,
} from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { Button, Link, Notification } from 'base-ui'

import {
  type ContactFormState,
  useContactFormApiPostMutation,
} from '../../../../contact-form'
import { FriendlyCaptchaChallenge } from '../../../../friendly-captcha'
import {
  type OpenableControls,
  useOpenableControls,
} from '../../../../generic-hooks'
import { LoginRegistrationUserLoginModal } from '../../../../login-registration'
import { refetchUseApiOrdersSwrGetOrders } from '../../../../order'
import { useDirectContactFormLoginModal } from '../../../hooks/useDirectContactFormLoginModal'
import { useDirectContactFormUpdateFormStateWithUserData } from '../../../hooks/useDirectContactFormUpdateFormStateWithUserData'
import { useDirectFormDialogContext } from '../DirectContactFormDialogContext'
import { DirectContactFormEmailInputField } from '../DirectContactFormEmailInputField'
import { DirectContactFormMessageInputField } from '../DirectContactFormMessageInputField'
import { DirectContactFormPhoneNumberField } from '../DirectContactFormPhoneNumberField'
import { DirectContactFormSelectSubjectField } from '../DirectContactFormSelectSubjectField'
import { DirectContactFormSubmitButton } from '../DirectContactFormSubmitButton'
import { DirectContactFormTextInfoSection } from '../DirectContactFormTextInfoSection'

const notificationTexts = (
  notificationText: string,
  intl: IntlShape,
): ReactNode => {
  const emailAddress = intl.formatMessage({
    id: 'contactForm.label.email.invitation.email',
  })

  const link = (text: ReactNode[]): ReactElement => (
    <Link className="text-dark-primary-max" href={`mailto:${emailAddress}`}>
      {text}
    </Link>
  )

  if (notificationText === 'accountDeletion') {
    return intl.formatMessage(
      { id: 'direct.contact.form.account.delete.notification.text' },
      { link },
    )
  }

  if (notificationText === 'advertising') {
    return intl.formatMessage(
      { id: 'direct.contact.form.advertising.notification.text' },
      { link },
    )
  }

  if (notificationText === 'addressChange') {
    return intl.formatMessage(
      { id: 'direct.contact.form.address.change.notification.text' },
      { link },
    )
  }

  return intl.formatMessage(
    { id: 'direct.contact.form.account.delete.notification.text' },
    { link },
  )
}

export const DirectContactFormSendMessageTypeForm: FC<{
  formState: ContactFormState
  notificationText: string
  onFormSubmittedWithSuccess: () => void
  userModalControls: OpenableControls
}> = ({
  formState,
  notificationText,
  onFormSubmittedWithSuccess,
  userModalControls,
}) => {
  const intl = useIntl()

  const { setSubject } = useDirectFormDialogContext()

  formState.use()

  const submitMutation = useContactFormApiPostMutation()

  const loginModalControls = useOpenableControls()

  const isLoggedIn = useDirectContactFormLoginModal(loginModalControls)

  useDirectContactFormUpdateFormStateWithUserData(formState)

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, formState.model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState],
  )

  return (
    <>
      <p className="mb-2 text-xs text-dark-primary-strong">
        <FormattedMessage id="general.form.label.mandatoryFieldIndicator" />
      </p>

      {isLoggedIn ? null : (
        <DirectContactFormEmailInputField formState={formState} />
      )}

      <DirectContactFormPhoneNumberField formState={formState} />

      <DirectContactFormSelectSubjectField formState={formState} />

      {isLoggedIn ? (
        <DirectContactFormMessageInputField formState={formState} />
      ) : (
        <Notification
          data-qa-id="contact-form-name-change-notification"
          icon
          severity="info"
          title={intl.formatMessage({
            id: 'message.center.thread.infoReplyTime.title',
          })}
        >
          {notificationTexts(notificationText, intl)}
        </Notification>
      )}

      <DirectContactFormTextInfoSection />

      <FriendlyCaptchaChallenge
        className="mb-3.5"
        doneCallback={captchaDoneCallback}
        startMode="auto"
        whenThisPropChangesResetCaptcha={submitMutation.error}
      />

      {isLoggedIn ? (
        <DirectContactFormSubmitButton
          formState={formState}
          isFriendlyCaptchaDone={isFriendlyCaptchaDone}
          modalControls={userModalControls}
          onFormSubmittedWithSuccess={onFormSubmittedWithSuccess}
          submitMutation={submitMutation}
        />
      ) : (
        <div className="flex flex-col justify-center gap-3 tablet:flex-row">
          <Button
            className="mb-2 w-full tablet:w-1/2"
            data-qa-id="contact-form-submit-cancel-button"
            onClick={() => {
              setSubject('')
              userModalControls.handleClose()
            }}
            variant="outlined"
          >
            <FormattedMessage id="general.button.cancel" />
          </Button>
        </div>
      )}

      <LoginRegistrationUserLoginModal
        afterLoggedIn={(): void => {
          void refetchUseApiOrdersSwrGetOrders()
          loginModalControls.handleClose()
        }}
        onClose={loginModalControls.handleClose}
        open={loginModalControls.isOpen}
      />
    </>
  )
}
